import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { useAuthContext } from "contexts/AuthContext"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import { ENABLE_ACCOUNT_SETUPS_AUDIOS } from "common/constants"
import { PLACEMENT_STATUS_LOG_TYPES } from "pages/student/placements/common/constants"
import { styles } from "../styles/PlacementLevel.styles"
import { useTranslation } from "react-i18next"
import React, { useEffect, useRef, useState } from "react"
import useApi from "modules/placements"
import { routes } from "common/navigationRoutes"
import LoaderModal from "components/UI/LoaderModal"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import { usePlacementContext } from "contexts/PlacementContext"
import { accountSetupAudios, getStartedSound, goBackSound } from "utils/sounds"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import useSound from "hooks/useSound"
import FinishTest from "components/PlacementTest/FinishTest"
import { isRunningFromIframe } from "common/functions"
import { LANGUAGES_ASSETS } from "../../../../services/config"
import { maxWidth600 } from "themes/default/breakpoints"

const smashyImg = require("../assets/smashy.gif")
const smashorImg = require("../assets/smashor.gif")

export const staticLevel = {
  levelName: "Level A 1.1 – What will you find here?",
  levelDescription: `Say your name and age
Spell names and words
Talk about birthdays and seasons
Say where people are from and where they live
Name classroom objects and describe them
Count from 1 to 100 and talk about time and schedules
Talk about your family, pets, and simple professions
Say what you like and describe people using basic words`,
}

export default function PlacementLevel({
  modules = [],
  onStartLevel,
  viewMode = ACTIVITY_VIEW_MODES.STUDENT,
  open = true,
}) {
  const { t, i18n } = useTranslation()
  const {
    placementModules,
    placementId,
    placementTestId,
    setPlacementModules,
    setPlacementId,
    setPlacementTestId,
    updatePlacementTest,
    updateModulesStatus,
    finishPlacementTest,
  } = usePlacementContext()
  const { accountType, userData, audioSettings } = useAuthContext()
  const { getModules, checkDetails, addStatusLog } = useApi()
  const [levels, setLevels] = useState([])
  const [levelIndex, setLevelIndex] = useState(-1)
  const MySwal = withReactContent(Swal)
  const [loading, setLoading] = useState(false)
  const [randomMessageNumber, setRandomMessageNumber] = useState(
    Math.floor(Math.random() * 5) + 1
  )
  const audioRef = useRef()
  const [playContinue] = useSound(getStartedSound, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [playBack] = useSound(goBackSound, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [showPlacementFinishedAlert, setShowPlacementFinishedAlert] =
    useState(false)
  const [placementExpectedLevel, setPlacementExpectedLevel] = useState(null)
  const theme = useTheme()
  const matches = useMediaQuery(maxWidth600)

  useEffect(() => {
    if (viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST) {
      setLevels(modules || [])
    }
  }, [modules, viewMode])

  useEffect(() => {
    if (!open || loading) {
      audioRef?.current?.pause()
      return
    }
    setLoading(true)
    if (!placementModules || placementModules.length === 0) {
      loadData()
    } else {
      setLevels(placementModules)
      const firstIncompleteLevelIndex = placementModules.findIndex(
        (level) =>
          level.placementTestStatusId <= PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
      )
      if (firstIncompleteLevelIndex !== -1) {
        // set new random message
        setRandomMessageNumber(Math.floor(Math.random() * 5) + 1)
        setLevelIndex(firstIncompleteLevelIndex)
      }
      setLoading(false)
    }
    return () => {
      audioRef?.current?.pause()
    }
  }, [open, placementModules])

  useEffect(() => {
    if (levelIndex === 0) {
      playAudio()
    }
    return () => {
      audioRef?.current?.pause()
    }
  }, [levelIndex])

  const playAudio = () => {
    if (!ENABLE_ACCOUNT_SETUPS_AUDIOS) return
    const curlang = i18n?.language || "en-US"
    const folder =
      accountType < 7
        ? "elementary"
        : accountType < 13
          ? "secondary"
          : "university"
    const source = `${LANGUAGES_ASSETS}/sounds/createAccount/${curlang}/${folder}/${accountSetupAudios.whatWouldYouLikeToDo}`
    const audio = new Audio(source)
    audio.play()
    audioRef.current = audio
  }

  const loadData = async () => {
    MySwal.fire({
      html: <LoaderModal />,
      showCancelButton: false,
      showConfirmButton: false,
    })
    // if query param placementId and placementId === -1
    let pId = placementId
    let ptId = placementTestId
    if (placementId === -1) {
      const queryParams = new URLSearchParams(window.location.search)
      if (queryParams.get("placementId")) {
        pId = parseInt(queryParams.get("placementId")) || 0
        setPlacementId(pId)
      }
      if (queryParams.get("placementTestId")) {
        ptId = parseInt(queryParams.get("placementTestId")) || 0
        setPlacementTestId(ptId)
      }
    }

    const [modulesDetails, response] = await Promise.all([
      checkDetails({ placementTestId: ptId }),
      getModules({
        placementId: pId,
        translationsLanguageId: userData.language,
      }),
    ])
    const modules = response || []
    if (modulesDetails?.placement?.grades?.length > 0 && modules.length > 0) {
      // recorre todos los modules y asignale los grades y progress
      modules.forEach((module) => {
        const grade = modulesDetails.placement.grades.find(
          (g) => g.levelId === module.moduleId
        )
        if (grade) {
          module.grade = grade.grade
          module.progress = grade.progress
          module.placementTestResultId = grade.placementTestResultId
          module.placementTestStatusId = grade.placementTestStatusId
        }
      })
    }
    setLevels(modules)
    setPlacementModules(modules)
    const firstIncompleteLevelIndex = modules?.findIndex
      ? modules.findIndex(
        (level) =>
          level.placementTestStatusId <= PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
      )
      : -1
    if (firstIncompleteLevelIndex !== -1) {
      setLevelIndex(firstIncompleteLevelIndex)
      // check current module progress
      const currentProgress = modules[firstIncompleteLevelIndex].progress
      console.log("🚀 ~ loadData ~ currentProgress:", currentProgress)
      if (currentProgress > 0) {
        if (currentProgress === 100) {
          // module is completed, update test and redirect to next module
          const levelCompleted = modules[firstIncompleteLevelIndex]
          const result = await updateModulesStatus({
            allModules: modules,
            currentModuleIndex: firstIncompleteLevelIndex,
            addLogs: false,
          })
          await updatePlacementTest({
            module: levelCompleted,
            modules: result?.modules || modules,
            logs: result?.logs || [],
          })
          const isExpectedToBeCompleted = result?.isExpectedToBeCompleted
          setPlacementExpectedLevel(result?.expectedLevel || levelCompleted)
          if (!isExpectedToBeCompleted) {
            setLevels(result?.modules)
            setPlacementModules(result?.modules)
            const newLevelIndex = result?.modules.findIndex(
              (level) =>
                level.placementTestStatusId <=
                PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
            )
            if (newLevelIndex !== -1) {
              setLevelIndex(newLevelIndex)
            }
          } else {
            // finish placement test
            await finishPlacementTest({
              module: result?.expectedLevel || levelCompleted,
              modules: result?.modules || modules,
            })
            setShowPlacementFinishedAlert(true)
          }
          MySwal.close()
          setLoading(false)
          return
        }
        const level = modules[firstIncompleteLevelIndex]
        const runningFromIframe = isRunningFromIframe()
        const activityUrl = `${routes.ACTIVITY}?placementId=${placementId}&placementTestId=${placementTestId}&moduleId=${level.moduleId}&view=${ACTIVITY_VIEW_MODES.PLACEMENT_TEST}`
        const mainDomain = window.location.origin + process.env.REACT_APP_MS_REDIRECT_URI
        console.log(`${mainDomain}${activityUrl}`)
        if (runningFromIframe && window.parent) {
          window.parent.location.href = `${mainDomain}${activityUrl}&authOrigin=parent`
        } else {
          window.location.href = `${mainDomain}${activityUrl}`
        }
        return
      }
    } else {
      setLevelIndex(0)
    }
    MySwal.close()
    setLoading(false)
  }

  const startLevel = () => {
    const level = levels?.[levelIndex]
    if (!level) return
    if (
      level.placementTestStatusId !== PLACEMENT_STATUS_LOG_TYPES.SUCCESS &&
      level.placementTestStatusId !== PLACEMENT_STATUS_LOG_TYPES.FAILED &&
      level.placementTestStatusId !== PLACEMENT_STATUS_LOG_TYPES.REASSIGNED
    ) {
      addStatusLog({
        placementTestResultId: level?.placementTestResultId,
        statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
      })
    }
    if (typeof onStartLevel === "function") {
      onStartLevel({
        levelId: level.moduleId,
        placementModules: levels,
      })
    } else {
      const runningFromIframe = isRunningFromIframe()
      const activityUrl = `${routes.ACTIVITY}?placementId=${placementId}&placementTestId=${placementTestId}&moduleId=${level.moduleId}&view=${ACTIVITY_VIEW_MODES.PLACEMENT_TEST}`
      const mainDomain = window.location.origin + process.env.REACT_APP_MS_REDIRECT_URI
      console.log(`${mainDomain}${activityUrl}`)
      if (runningFromIframe && window.parent) {
        window.parent.location.href = `${mainDomain}${activityUrl}&authOrigin=parent`
      } else {
        window.location.href = `${mainDomain}${activityUrl}`
      }
    }
  }

  const skipLevel = () => {
    playContinue()
    const level = levels?.[levelIndex]
    if (!level) return
    const currentIndex = levels.findIndex((m) => m.moduleId === level?.moduleId)
    if (currentIndex >= 0) {
      // Skip to the next level
      const nextLevel = levels[currentIndex + 1]
      if (nextLevel) {
        addStatusLog({
          placementTestResultId: level?.placementTestResultId,
          statusId: PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_STUDENT,
          moveToModuleId: nextLevel?.moduleId,
        })
        // set next module in progress
        addStatusLog({
          placementTestResultId: nextLevel?.placementTestResultId,
          statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
        })
        // update status in module
        const newLevels = [...levels]
        newLevels[currentIndex].placementTestStatusId =
          PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_STUDENT
        newLevels[currentIndex + 1].placementTestStatusId =
          PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS
        setLevels(newLevels)
        // set new random message
        setRandomMessageNumber(Math.floor(Math.random() * 5) + 1)
        setLevelIndex(currentIndex + 1)
      }
    }
  }

  const goBackLevel = () => {
    playBack()
    const level = levels?.[levelIndex]
    if (!level) return
    const currentIndex = levels.findIndex((m) => m.moduleId === level?.moduleId)
    if (currentIndex >= 0) {
      // go back one level
      const prevLevel = levels[currentIndex - 1]
      if (prevLevel) {
        addStatusLog({
          placementTestResultId: prevLevel?.placementTestResultId,
          statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
        })
        setRandomMessageNumber(Math.floor(Math.random() * 5) + 1)
        setLevelIndex(currentIndex - 1)
      }
    }
  }

  if (loading) {
    return null
  }

  return (
    <Stack
      direction={matches ? "column" : "row"}
      spacing={matches ? 1.5 : 3}
      justifyContent={"center"}
      alignItems={"center"}
      sx={styles.container(modules.length > 0)}
    >
      <Dialog
        open={showPlacementFinishedAlert}
        disableEscapeKeyDown
        onClose={(evt, reason) => {
          if (reason !== "backdropClick") {
            setShowPlacementFinishedAlert(false)
          }
        }}
        PaperProps={{
          sx: {
            maxWidth: "75%",
          },
        }}
      >
        <DialogContent>
          <FinishTest
            accountType={accountType}
            onClose={() => {
              setShowPlacementFinishedAlert(false)
              const runningFromIframe = isRunningFromIframe()
              // return to missions
              if (runningFromIframe && window.parent) {
                window.parent.location.href = `${window.location.origin}/missions?authOrigin=parent`
              } else {
                window.location.href = `${window.location.origin}${process.env.REACT_APP_MS_REDIRECT_URI}/missions`
              }
            }}
            levelName={placementExpectedLevel?.moduleName || ""}
          />
        </DialogContent>
      </Dialog>
      <Box
        component="img"
        src={accountType < 7 ? smashyImg : smashorImg}
        alt="Smashy Character"
        sx={styles.smashyCharacter}
      />
      <Stack sx={styles.content} spacing={2} alignItems={"center"}>
        <Stack sx={styles.box}>
          <Typography
            variant="span"
            fontWeight={"bold"}
            sx={{
              textTransform: "capitalize",
              color: theme.palette.common.black,
            }}
          >
            {`${t("level_what_will_you_find_here", {
              level: levels?.[levelIndex]?.moduleName || "",
            })}`}
          </Typography>
          <Typography
            variant="span"
            fontWeight={"bold"}
            sx={{ color: theme.palette.common.black }}
          >
            {t("in_this_level_you_will_show")}
          </Typography>
          <br />
          <Typography
            variant="span"
            sx={styles.levelDescription}
            component={"div"}
            dangerouslySetInnerHTML={{
              __html: levels?.[levelIndex]?.moduleObjective || "",
            }}
          />
          {levelIndex < (levels?.length || 0) - 1 &&
            levels?.[levelIndex + 1]?.placementTestStatusId <=
            PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS && (
              <React.Fragment>
                <br />
                <Typography
                  variant="span"
                  fontWeight={"bold"}
                  sx={{ color: theme.palette.common.black }}
                >
                  {t(`level_message_0${randomMessageNumber}`)}
                </Typography>
                <br />
              </React.Fragment>
            )}
        </Stack>
        <Stack justifyContent={"center"} alignItems={"center"} spacing={1}>
          {levelIndex < (levels?.length || 0) - 1 &&
            levels?.[levelIndex + 1]?.placementTestStatusId <=
            PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS && (
              <Typography
                variant="span"
                fontWeight={"bold"}
                sx={{ color: theme.palette.common.black }}
              >
                {t(`what_would_you_like_to_do`)}
              </Typography>
            )}
          <Stack spacing={2} direction={"row"}>
            {levels?.[levelIndex]?.allowLevelSkip &&
              levels?.[levelIndex - 1]?.placementTestStatusId ===
              PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_STUDENT && (
                <IconButton sx={styles.goBackButton} onClick={goBackLevel}>
                  <ArrowBackIcon color="primary" />
                </IconButton>
              )}
            {levels?.[levelIndex]?.allowLevelSkip &&
              levelIndex < (levels?.length || 0) - 1 &&
              (levels?.[levelIndex + 1]?.placementTestStatusId <=
                PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS ||
                levels?.[levelIndex + 1]?.placementTestStatusId ===
                PLACEMENT_STATUS_LOG_TYPES.SKIPPED_BY_STUDENT) && (
                <Button
                  variant="outlined"
                  color="primary"
                  sx={styles.takeTestBtn}
                  onClick={() => {
                    playContinue()
                    skipLevel()
                  }}
                >
                  {t("skip_this_level")}
                </Button>
              )}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              sx={styles.takeTestBtn}
              onClick={() => {
                playContinue()
                startLevel()
              }}
            >
              {t("begin_test")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}
